import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import * as requestUrl from "constants/request";
import { DEFAULT_OCTET_STREAM } from "utility/mimeTypes";
import {
  useBlobPreview,
  type PreviewKind,
  type PreviewState,
} from "hooks/useBlobPreview";
import { usePdfPageRenderer } from "hooks/usePdfPageRenderer";
import { useRequest } from "hooks/useRequest";
import classes from "./StatementPreviewDialog.module.scss";

export type { PreviewKind, PreviewState };

const STATEMENT_PREVIEW_URL = new URL(
  `${requestUrl.FT_STATEMENTS}/preview`,
  requestUrl.FT_REQUEST_BASE_URL,
).toString();

interface StatementPreviewDialogProps {
  accountInformationId?: number | null;
  isOpen: boolean;
  onClose: () => void;
  statement: {
    path: string;
    name: string;
    fileTitle: string;
    contentType: string | null;
  } | null;
}

export const StatementPreviewDialog: React.FC<StatementPreviewDialogProps> = ({
  accountInformationId,
  isOpen,
  onClose,
  statement,
}) => {
  const { t } = useTranslation(["common", "eStatements"]);
  const { request } = useRequest();
  const dialogRef = useRef<HTMLElement>(null);
  const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
  const pdfCanvasWrapperRef = useRef<HTMLDivElement>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasRenderError, setHasRenderError] = useState(false);

  const fetchBlob = useCallback(async () => {
    const response = await request.post<Blob>(
      STATEMENT_PREVIEW_URL,
      { accountInformationId, filePaths: [statement?.path ?? ""] },
      { responseType: "blob" },
    );
    const responseContentType = response.headers?.["content-type"];
    const contentTypeHeader =
      typeof responseContentType === "string"
        ? responseContentType
        : DEFAULT_OCTET_STREAM;
    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data], {
            type: contentTypeHeader,
          });
    return {
      blob,
      contentType: contentTypeHeader,
    };
  }, [accountInformationId, request, statement?.path]);

  const { hasLoadError, isLoading, pageCount, pdfDocument, previewState } =
    useBlobPreview({
      enabled: isOpen && !!statement?.path,
      fetchBlob,
      fileName: statement?.name || statement?.fileTitle || "statement_preview",
      contentTypeHint: statement?.contentType ?? null,
      nameHint: statement?.name ?? null,
      pathHint: statement?.path ?? null,
    });

  const { hasRenderedPdf, isRenderingPdf } = usePdfPageRenderer({
    canvasRef: pdfCanvasRef,
    canvasWrapperRef: pdfCanvasWrapperRef,
    pageNumber,
    pdfDocument,
    previewState,
    onError: () => setHasRenderError(true),
  });

  useEffect(() => {
    if (!isOpen) {
      setPageNumber(1);
      setHasRenderError(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const currentDialog = dialogRef.current;

    if (!currentDialog) {
      return;
    }

    const handleClosed = (event: Event) => {
      const customEvent = event as CustomEvent<boolean>;
      if (customEvent.detail) {
        onClose();
      }
    };

    currentDialog.addEventListener("closed", handleClosed as EventListener);

    return () => {
      currentDialog.removeEventListener(
        "closed",
        handleClosed as EventListener,
      );
    };
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  const hasError = hasLoadError || hasRenderError;

  const showPreviousPage = () => {
    setPageNumber((current) => Math.max(1, current - 1));
  };

  const showNextPage = () => {
    setPageNumber((current) => Math.min(pageCount, current + 1));
  };

  return (
    <ft-dialog
      hideActions
      open
      ref={dialogRef}
      scrimClickAction=""
      heading={t("eStatements:preview_statement", "File preview")}
      style={
        {
          "--mdc-dialog-max-width": "min(98vw, 1200px)",
          "--mdc-dialog-max-height": "96vh",
          "--mdc-dialog__content-overflow": "hidden",
        } as React.CSSProperties
      }
    >
      <div className={classes.Container}>
        <span className={classes.CloseButton}>
          <ft-icon onClick={onClose}>close</ft-icon>
        </span>
        <div role="document" className={classes.Contents}>
          {isLoading ? (
            <div className={classes.PreviewFallback} role="status">
              {t("common:loading", "Loading...")}
            </div>
          ) : null}

          {!isLoading && hasError ? (
            <div className={classes.PreviewFallback} role="alert">
              {t(
                "eStatements:preview_load_error",
                "We couldn't load this file preview right now.",
              )}
            </div>
          ) : null}

          {!isLoading && !hasError && previewState?.kind === "unsupported" ? (
            <div className={classes.PreviewFallback} role="alert">
              {t(
                "eStatements:preview_unsupported",
                "This file type cannot be previewed here.",
              )}
            </div>
          ) : null}

          {!isLoading && !hasError && previewState?.kind === "image" ? (
            <img
              src={previewState.url}
              alt={previewState.fileName}
              className={classes.PreviewImage}
            />
          ) : null}

          {!isLoading && !hasError && previewState?.kind === "pdf" ? (
            <div className={classes.PdfPreviewSection}>
              <div
                ref={pdfCanvasWrapperRef}
                className={classes.PdfCanvasWrapper}
              >
                {isRenderingPdf && !hasRenderedPdf ? (
                  <div className={classes.PreviewFallback} role="status">
                    {t("common:loading", "Loading...")}
                  </div>
                ) : null}
                <canvas
                  ref={pdfCanvasRef}
                  className={classes.PdfCanvas}
                  aria-label={previewState.fileName}
                />
              </div>
              <div className={classes.PdfPaginationBar}>
                <button
                  type="button"
                  className={classes.PaginationButton}
                  onClick={showPreviousPage}
                  disabled={pageNumber <= 1}
                  title={t("common:previous", "Previous")}
                  aria-label={t("common:previous", "Previous")}
                >
                  <ft-icon>navigate_before</ft-icon>
                </button>
                <div className={classes.PdfPageIndicator}>
                  {t("eStatements:page_indicator", {
                    defaultValue: "{{page}} of {{count}}",
                    page: pageNumber,
                    count: pageCount,
                  })}
                </div>
                <button
                  type="button"
                  className={classes.PaginationButton}
                  onClick={showNextPage}
                  disabled={pageNumber >= pageCount}
                  title={t("common:next", "Next")}
                  aria-label={t("common:next", "Next")}
                >
                  <ft-icon>navigate_next</ft-icon>
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </ft-dialog>
  );
};
